// Stage 5 C-A — Client Portal router.
//
// Locked five-destination information architecture (§3):
//   Overview / Programme / Design / Files / Actions.
// Paid Services (§18–§23) are CONTEXTUAL detail routes reached from
// Overview / Actions / Files.  No permanent Paid Services /
// Commercial / Contracts / Payments nav entry.
// No standalone portal inbox (§26).  No 562-07 (§24).
// Audience-separated auth (§4): portal session ≠ admin access.
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Route, Routes, useNavigate } from "react-router-dom";
import {
    Home, Calendar, PenLine, FolderOpen, CheckCircle2, LogOut,
} from "lucide-react";
import { portalApi, resolveProjectName } from "./portalApi";
import { Button, Pill } from "./PortalUi";
import PortalOverview from "./PortalOverview";
import PortalProgramme from "./PortalProgramme";
import { PortalDesign, PortalDesignIssue } from "./PortalDesign";
import { PortalFiles } from "./PortalFiles";
import { PortalFileCategoriesProvider } from "./context/PortalFileCategoriesContext";
import { PortalActions, PortalRfiDetail } from "./PortalActions";
import {
    PortalPackageDetail, PortalContractPosition, PortalContractChange,
    PortalCertificateDetail, PortalConstructionProgress,
} from "./PortalPaidServices";
import { usePageMeta } from "../../components/Layout";

// ---------- Portal login (§4/§61 — Portal ID + Password only) ----------

// Crockford-32 (no I, L, O, U) — matches the backend
// `_PUBLIC_ID_RE` enforced at `/api/portal/auth/login`.
const PORTAL_ID_RE = /^SG4-[0-9A-HJKMNP-TV-Z]{5}-[0-9A-HJKMNP-TV-Z]{5}$/;

export function PortalLogin() {
    usePageMeta({ title: "Sign in | Studio G4 Portal", robots: "noindex,nofollow" });
    const [portalId, setPortalId] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const idTrimmed = portalId.trim().toUpperCase();
    const idValid = PORTAL_ID_RE.test(idTrimmed);
    const canSubmit = idValid && password.length > 0 && !busy;

    function handleIdBlur() {
        // Normalise on blur so the submit gate reflects the server
        // expectation.  Also strips trailing whitespace clients
        // sometimes paste in from emails.
        setPortalId(portalId.trim().toUpperCase());
    }

    async function submit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true); setErr("");
        try {
            await portalApi.login(idTrimmed, password);
            navigate("/portal", { replace: true });
        } catch (ex) {
            const status = ex?.response?.status;
            const detail = ex?.response?.data?.detail;
            if (status === 429) {
                const retryAfter = parseInt(
                    ex.response.headers?.["retry-after"] || "0", 10);
                const mins = Math.max(1, Math.ceil(retryAfter / 60));
                setErr(`Too many failed attempts. Try again in ${mins} `
                        + `minute${mins === 1 ? "" : "s"}.`);
            } else if (status === 401
                        && typeof detail === "string"
                        && detail.includes("updated login credentials")) {
                // Pass legacy-UUID hint through verbatim so the user
                // sees the explicit format correction.
                setErr(detail);
            } else if (status === 401) {
                // Generic shape — never echo the server message
                // (enumeration safety).
                setErr("Invalid Portal ID or password.");
            } else if (status && status >= 500) {
                setErr("Something went wrong. Please try again.");
            } else {
                setErr("Invalid Portal ID or password.");
            }
        } finally { setBusy(false); }
    }

    return (
        <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center p-4" data-testid="portal-login">
            <form onSubmit={submit} className="w-full max-w-md bg-white rounded-lg border border-[#E5E2DC] p-8 shadow-sm">
                <div className="mb-6">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8543F] font-manrope font-semibold">Studio G4</div>
                    <h1 className="mt-1 font-manrope text-[26px] font-semibold text-charcoal">Client portal</h1>
                    <p className="mt-1 text-[12.5px] text-[#6B6B67]">Sign in with your Portal ID and password.</p>
                </div>
                {err && (
                    <div className="mb-4 text-[12.5px] text-[#8C2E1E] bg-[#F6E0DA] border border-[#E9C6BE] rounded p-2.5"
                         data-testid="portal-login-err">
                        {err}
                    </div>
                )}
                <label className="block mb-4">
                    <span className="text-[11px] font-manrope font-semibold uppercase tracking-[0.1em] text-[#6B6B67]">Portal ID</span>
                    <input data-testid="portal-login-project-id"
                        className="mt-1 w-full px-3 py-2 border border-[#D7D4CC] rounded text-[14px] font-mono uppercase focus:outline-none focus:border-[#A8543F]"
                        type="text" value={portalId}
                        onChange={e => setPortalId(e.target.value)}
                        onBlur={handleIdBlur}
                        placeholder="SG4-ABCDE-FGHIJ"
                        autoComplete="off" required />
                    {portalId.length > 0 && !idValid && (
                        <span className="mt-1 block text-[11.5px] text-[#8C2E1E]"
                              data-testid="portal-login-id-hint">
                            Portal ID must follow the format
                            SG4-XXXXX-XXXXX.
                        </span>
                    )}
                </label>
                <label className="block mb-6">
                    <span className="text-[11px] font-manrope font-semibold uppercase tracking-[0.1em] text-[#6B6B67]">Password</span>
                    <input data-testid="portal-login-password"
                        className="mt-1 w-full px-3 py-2 border border-[#D7D4CC] rounded text-[14px] focus:outline-none focus:border-[#A8543F]"
                        type="password" value={password} onChange={e => setPassword(e.target.value)}
                        autoComplete="current-password" required />
                </label>
                <Button type="submit" variant="primary" disabled={!canSubmit} testid="portal-login-submit" className="w-full">
                    {busy ? "Signing in…" : "Log in"}
                </Button>
                <p className="mt-6 text-[11.5px] text-[#8A8A84] leading-relaxed">
                    Access to this portal is provided by Studio G4. If you're unsure of your Portal ID, please contact your project team.
                </p>
            </form>
        </div>
    );
}

// ---------- Protected shell (§3 — five canonical destinations) ----------

const NAV = [
    { to: "/portal",           label: "Overview",  end: true, id: "overview",  Icon: Home },
    { to: "/portal/programme", label: "Programme",            id: "programme", Icon: Calendar },
    { to: "/portal/design",    label: "Design",               id: "design",    Icon: PenLine },
    { to: "/portal/files",     label: "Files",                id: "files",     Icon: FolderOpen },
    { to: "/portal/actions",   label: "Actions",              id: "actions",   Icon: CheckCircle2 },
];

export function PortalLayout() {
    const [me, setMe] = useState(null);
    const [project, setProject] = useState(null);
    const [ready, setReady] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        portalApi.me().then(r => { setMe(r.data); setReady(true); })
            .catch(() => navigate("/portal/login", { replace: true }));
    }, [navigate]);
    useEffect(() => {
        if (!me) return;
        portalApi.overview().then(r => setProject(r.data.project)).catch(() => {});
    }, [me]);
    async function logout() {
        try { await portalApi.logout(); } catch {}
        navigate("/portal/login", { replace: true });
    }

    if (!ready) {
        return <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center text-[13px] text-[#6B6B67]" data-testid="portal-loading">Loading…</div>;
    }
    const SIDEBAR = (
        <aside className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static inset-y-0 left-0 z-30 w-72 h-screen overflow-y-auto bg-zinc-900 text-zinc-300 flex flex-col flex-shrink-0 shadow-2xl transition-transform`} data-testid="portal-sidebar">
            {/* Top row — logo + Client Portal pill, mirroring the
                reference layout.  Logo image preserved (scaled to
                max-w-[90px] per earlier hotfix). */}
            <div className="px-6 pt-7 pb-5 flex items-center justify-between gap-3">
                <img src="/SG4_Logo_Light.png" alt="Studio G4"
                     className="max-w-[108px] h-auto select-none" data-testid="portal-sidebar-logo" />
                <span className="bg-zinc-800 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-md text-zinc-400 border border-zinc-700/50"
                      data-testid="portal-sidebar-tag">
                    Client Portal
                </span>
            </div>

            {/* Nav — rounded-xl pills, terracotta-tinted active state. */}
            <nav className="flex-1 px-4 space-y-1.5 mt-2 overflow-y-auto">
                {NAV.map(n => {
                    const Icon = n.Icon;
                    return (
                        <NavLink key={n.id} to={n.to} end={n.end}
                            data-testid={`portal-nav-${n.id}`}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) =>
                                "flex items-center gap-3 px-4 py-3 rounded-xl font-manrope text-[15px] transition-colors group " +
                                (isActive
                                    ? "bg-[#A8543F]/10 text-[#A8543F] font-medium border border-[#A8543F]/20"
                                    : "text-zinc-300 hover:bg-zinc-800 hover:text-white border border-transparent")
                            }>
                            {({ isActive }) => (<>
                                <Icon size={18}
                                      className={isActive ? "text-[#A8543F]" : "text-zinc-500 group-hover:text-zinc-300"} />
                                <span>{n.label}</span>
                            </>)}
                        </NavLink>
                    );
                })}
            </nav>

            {/* Bottom area — project card + logout.  Per the reference
                the project card shows: client name → postcode → divider
                → Project ID.  `project_title` row deliberately omitted
                (user decision). */}
            <div className="p-6 mt-auto">
                {project && (
                    <div className="bg-zinc-800/80 rounded-2xl p-5 mb-4 border border-zinc-700/50 shadow-inner" data-testid="portal-sidebar-project-card">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Current Project</p>
                        <p className="text-white font-medium text-sm" data-testid="portal-sidebar-client-name">
                            {resolveProjectName(project) || me?.client_name}
                        </p>
                        {project.postcode && (
                            <p className="text-zinc-400 text-xs mt-0.5" data-testid="portal-sidebar-postcode">
                                {project.postcode}
                            </p>
                        )}
                        <div className="mt-4 pt-4 border-t border-zinc-700/50">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Project ID</p>
                            <p className="text-zinc-300 text-xs font-mono" data-testid="portal-sidebar-project-ref">
                                {project.project_ref}
                            </p>
                        </div>
                    </div>
                )}
                <button data-testid="portal-logout" onClick={logout}
                    className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors group">
                    <LogOut size={18} className="group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm font-medium">Log out</span>
                </button>
            </div>
        </aside>
    );
    return (
        <div className="h-screen bg-[#F5F3EE] flex overflow-hidden" data-testid="portal-shell">
            <button
                className="md:hidden fixed top-3 left-3 z-40 bg-zinc-900 text-white rounded px-3 py-1.5 text-[13px] font-manrope font-semibold"
                onClick={() => setMobileOpen(o => !o)}
                data-testid="portal-mobile-toggle"
                aria-label="Toggle navigation"
            >
                {mobileOpen ? "Close" : "Menu"}
            </button>
            {SIDEBAR}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 bg-black/40 z-20" onClick={() => setMobileOpen(false)} />
            )}
            <main className="flex-1 min-w-0 h-screen overflow-y-auto bg-[#F5F3EE] relative px-6 lg:px-12" data-testid="portal-main">
                <Outlet />
            </main>
        </div>
    );
}

// ---------- Router mount ----------

export default function PortalRoutes() {
    usePageMeta({ robots: "noindex,nofollow" });
    return (
        <Routes>
            <Route path="login" element={<PortalLogin />} />
            <Route path="" element={
                <PortalFileCategoriesProvider>
                    <PortalLayout />
                </PortalFileCategoriesProvider>
            }>
                <Route index element={<PortalOverview />} />
                <Route path="programme" element={<PortalProgramme />} />
                <Route path="design" element={<PortalDesign />} />
                <Route path="design/issues/:issueId" element={<PortalDesignIssue />} />
                <Route path="files" element={<PortalFiles />} />
                <Route path="files/:fileId" element={<PortalFiles />} />
                <Route path="files/:fileId/preview" element={<PortalFiles />} />
                <Route path="files/:fileId/versions" element={<PortalFiles />} />
                <Route path="actions" element={<PortalActions />} />
                <Route path="rfis/:rfiId" element={<PortalRfiDetail />} />
                {/* Paid Services — contextual overlays, NOT a permanent
                    top-level nav entry (§3 / §18). */}
                <Route path="paid-services/packages/:packageId" element={<PortalPackageDetail />} />
                <Route path="paid-services/contracts/:contractId" element={<PortalContractPosition />} />
                <Route path="paid-services/contract-changes/:changeId" element={<PortalContractChange />} />
                <Route path="paid-services/certificates/:certId" element={<PortalCertificateDetail />} />
                <Route path="paid-services/construction-progress" element={<PortalConstructionProgress />} />
            </Route>
        </Routes>
    );
}
